import {Injectable} from '@angular/core';
import {apiEndpoints, AutoGroupFilterList, CommonConstant, GenderEnum} from '../../app.constants';
import {GroupCondition} from '../../../models/group-condition';
import {GroupSearch} from '../../../models/group-search';
import {AddInfluencerGroup, Influencer} from '../../../models/influencer';
import {Observable} from 'rxjs/Observable';
import {BackendHttpService} from '../../core/backend-http.service';
import {ErrorTemplate, ValidationError} from '../../../models/ErrorTemplate';
import {InfluencerGroup} from '../../../models/influencer-group';
import {TranslateService} from '@ngx-translate/core';
import {ReferenceDataService} from '../reference-data/reference-data.service';
import {Router} from '@angular/router';
import {
  AutoGroupComparisonConfig,
  AutoGroupFilter,
  AutoGroupFilterConfig,
  AutoGroupFilterEnum, AutoGroupSourceEnum,
  GroupVersion,
  NewInfluencerGroup
} from '../../../models/auto-group/auto-group-setting';

export enum GroupTypeEnum {
  PREFERRED = 1,
  PROJECT = 2,
  TOPIC = 3,
  INDUSTRIES = 4,
  CAMPAIGNS = 5
}

export enum GroupConditionTypeEnum {
  MENTION = 'keyword',
  COUNTRY = 'country',
  REACH_MONTHLY = 'reach',
  ENGAGEMENT_MONTHLY = 'engagement',
  IMPACT_MONTHLY = 'impact',
  AUDIENCE_AGE = 'audience_age',
  AUDIENCE_GENDER = 'audience_gender',
  AUDIENCE_COUNTRY = 'audience_country',
  INFLUENCER_GENDER = 'gender'
}

@Injectable()
export class GroupService extends BackendHttpService<any> {
  constructor(protected router: Router,
              protected translate: TranslateService,
              private referenceData: ReferenceDataService) {

    super(router, translate);
  }

  public getInfluencerGroup(influencer_group_id: number) {
    return this.get(apiEndpoints.getInfluencerGroupDetails, {influencer_group_id: influencer_group_id})
      .map((res) => {
        if (!res || !res.data) {
          return null;
        }
        if (res.data.version === GroupVersion.V2) {
          return this.createNewGroup(influencer_group_id, res.data.name, res.data.description, res.data.sources, res.data.filters);
        } else if (Array.isArray(res.data.condition_list_object) && res.data.condition_list_object.length === 0) {
          return this.createNewGroup(influencer_group_id, res.data.influencer_group_name, res.data.influencer_group_description, null, null);
        } else {
          return this.normalizeInfluencerGroupResponse(res.data, influencer_group_id);
        }
      });
  }
  private createNewGroup(id: number, name: string, description: string, sources: any[], filters: any[]) {
    const influencerGroup = new NewInfluencerGroup();
    influencerGroup.id = id;
    influencerGroup.name = name;
    influencerGroup.description = description;
    influencerGroup.type = CommonConstant.NEW_AUTO_GROUP_TYPE;
    influencerGroup.sources = sources ? sources : [];
    influencerGroup.filters = filters ? this.denormalizeAutoGroupFilter(filters) : [];
    return influencerGroup;
  }

  private normalizeInfluencerGroupResponse(influencerGroupResponse: any, influencerGroupId: number) {
    let influencer_group_type_id;
    if (influencerGroupResponse.influencer_group_type_values.length > 0) {
      // on Redesign, only suppose single seletion for group type value
      const firstGroupTypeValue = influencerGroupResponse.influencer_group_type_values[0];
      influencerGroupResponse.influencer_group_type_values = [firstGroupTypeValue];
      influencer_group_type_id = Number(firstGroupTypeValue.id);
    }
    influencerGroupResponse.influencer_group_type_id = influencer_group_type_id;
    influencerGroupResponse.influencer_group_id = influencerGroupId;
    influencerGroupResponse.condition_list_object = this.denormalizeGroupConditionList(influencerGroupResponse.condition_list_object);
    return influencerGroupResponse;
  }

  private denormalizeGroupConditionList(conditionList: any) {
    return conditionList.map(c => {
      if (c.selectedConditionType.indexOf(GroupConditionTypeEnum.AUDIENCE_AGE) >= 0
        || c.selectedConditionType.indexOf(GroupConditionTypeEnum.AUDIENCE_GENDER) >= 0
        || c.selectedConditionType.indexOf(GroupConditionTypeEnum.AUDIENCE_COUNTRY) >= 0) {
        const conditionTypeAndValue = this.extractConditionTypeAndValue(c.selectedConditionType, c.selectedConditionComparisionValue);
        return {
          index: c.index,
          selectedConditionComparision: c.selectedConditionComparision,
          selectedConditionOperator: c.selectedConditionOperator,
          minPercent: conditionTypeAndValue.percent.toString(),
          selectedConditionComparisionValue: conditionTypeAndValue.value,
          selectedConditionType: c.selectedConditionType
        };
      } else if (c.selectedConditionType.indexOf(GroupConditionTypeEnum.COUNTRY) >= 0) {
        const country = this.referenceData.countries.find(co => co.name === c.selectedConditionComparisionValue);
        return Object.assign({}, c, {
          selectedConditionComparisionValue: country ? country.id.toString() : 0
        });
      } else {
        return c;
      }
    });
  }

  public normalizeGroupConditionList(conditionList: GroupCondition[]) {
    return conditionList.map(c => {
      if (this.isAudienceFilter(c.selectedConditionType)) {
        return {
          index: c.index,
          selectedConditionComparision: c.selectedConditionComparision,
          selectedConditionType: c.selectedConditionType,
          selectedConditionComparisionValue: `${c.selectedConditionComparisionValue}_${Number(c.minPercent) / 100}`,
          selectedConditionOperator: c.selectedConditionOperator
        };
      } else {
        return c;
      }
    });
  }

  public isAudienceFilter(conditionType) {
    return conditionType === GroupConditionTypeEnum.AUDIENCE_GENDER
      || conditionType === GroupConditionTypeEnum.AUDIENCE_AGE
      || conditionType === GroupConditionTypeEnum.AUDIENCE_COUNTRY;
  }

  private extractConditionTypeAndValue(conditionType, conditionValue) {
    const values = conditionValue.split('_');
    if (conditionType === GroupConditionTypeEnum.AUDIENCE_COUNTRY) {
      const country = this.referenceData.countries.find(c => c.code === values[0]);
      return {
        value: country ? country.id.toString() : null,
        percent: Number(values[1]) * 100
      };
    } else {
      return {
        value: values[0],
        percent: Number(values[1]) * 100
      };
    }
  }

  public updateInfluencerGroup(influencerGroup: any) {
    return this.post(apiEndpoints.updateInfluencerGroup, influencerGroup);
  }

  /**
   * update Auto Group Setting V2
   */
  public updateInfluencerGroupV2(influencerGroup: NewInfluencerGroup, companyId: number) {
    const formattedFilters = this.normalizeAutoGroupFilter(influencerGroup.filters);
    const formattedSources = influencerGroup.sources.filter(s => s.type !== AutoGroupSourceEnum.BRANDED_SIGNUP);
    const groupData = Object.assign({}, influencerGroup, {
      companyId,
      filters: formattedFilters,
      sources: formattedSources
    });
    delete groupData.version;
    return this.post(apiEndpoints.updateInfluencerGroupV2, groupData);
  }

  private normalizeAutoGroupFilter(filters: AutoGroupFilter[]) {
    const formattedFilters = [];
    filters.forEach((f) => {
      const filterConfig = AutoGroupFilterConfig[f.type];
      if (filterConfig.multiple && Array.isArray(f.secondValue)) {
        const multipleFilters = f.secondValue.map(s => ({
          index: f.index,
          type: f.type,
          comparison: AutoGroupComparisonConfig[f.comparison].operator,
          value: s
        }));
        formattedFilters.push(...multipleFilters);
      } else if (filterConfig.numOfCol === AutoGroupFilterList.multipleValueColumn) {
        const formattedSecondValue = this.getSecondFilterValue(f.type, f.secondValue);
        const combinedValue = `${formattedSecondValue}_${f.firstValue}`;
        formattedFilters.push({
          index: f.index,
          type: f.type,
          comparison: AutoGroupComparisonConfig[f.comparison].operator,
          value: combinedValue
        });
      } else {
        formattedFilters.push({
          index: f.index,
          type: f.type,
          comparison: AutoGroupComparisonConfig[f.comparison].operator,
          value: f.secondValue
        });
      }
    });
    formattedFilters.sort((f1, f2) => f1.index - f2.index);
    return formattedFilters;
  }

  private denormalizeAutoGroupFilter(filters: any[]): AutoGroupFilter[] {
    const formattedFilters: AutoGroupFilter[] = [];
    filters.forEach((f) => {
      const isExisted = formattedFilters.findIndex(fi => fi.type === f.type && fi.comparison === f.comparison) >= 0;
      if (isExisted) {
        return;
      }
      const filterConfig = AutoGroupFilterConfig[f.type];
      if (filterConfig.multiple) {
        const filterValues = filters.filter(fi => fi.type === f.type).map(filter => Number(filter.value));
        const groupingFilter = {
          index: f.index,
          type: f.type,
          comparison: f.comparison,
          firstValue: null,
          secondValue: filterValues
        };
        formattedFilters.push(groupingFilter);
      } else if (filterConfig.numOfCol === AutoGroupFilterList.multipleValueColumn) {
        const normalizedFilter = {
          index: f.index,
          type: f.type,
          comparison: f.comparison,
          firstValue: Number(this.denormalizeFirstValue(f.value)),
          secondValue: this.denormalizeSecondValue(f.type, f.value)
        };
        formattedFilters.push(normalizedFilter);
      } else {
        formattedFilters.push({
          index: f.index,
          type: f.type,
          comparison: f.comparison,
          firstValue: null,
          secondValue: Number(f.value)
        });
      }
    });
    const result = formattedFilters.map(ff => Object.assign({}, ff, {
      comparison: this.getComparisonByOperator(ff.comparison, ff.type)
    }));
    result.sort((f1, f2) => f1.index - f2.index);
    return result;
  }

  private getSecondFilterValue(filterType, secondValue) {
    let selectedValue = null;
    switch (filterType) {
      case AutoGroupFilterEnum.COUNTRY:
      case AutoGroupFilterEnum.AUDIENCE_COUNTRY:
        const country = this.referenceData.countries.find( c => c.id === secondValue);
        if (country) {
          selectedValue = country.code;
        }
        break;
      case AutoGroupFilterEnum.INFLUENCER_GENDER:
      case AutoGroupFilterEnum.AUDIENCE_GENDER:
        if (secondValue === GenderEnum.MALE) {
          selectedValue = AutoGroupFilterList.genders.male;
        } else if (secondValue === GenderEnum.FEMALE) {
          selectedValue = AutoGroupFilterList.genders.female;
        } else {
          selectedValue = AutoGroupFilterList.genders.online_media;
        }
        break;
      case AutoGroupFilterEnum.AUDIENCE_AGE:
        const ageRange = AutoGroupFilterList.audienceAgeList.find(age => age.id === secondValue);
        if (ageRange) {
          selectedValue = ageRange.formatted;
        }
        break;
      case AutoGroupFilterEnum.AVG_INTERACTION_PER_POST:
      case AutoGroupFilterEnum.AVG_ENGAGEMENT_PER_POST:
      case AutoGroupFilterEnum.AVG_REACH_PER_POST:
      case AutoGroupFilterEnum.FOLLOWERS:
      case AutoGroupFilterEnum.PLATFORM:
        selectedValue = secondValue;
        break;
    }
    return selectedValue;
  }

  private denormalizeFirstValue(value) {
    const valueArr = value.split('_');
    if (valueArr.length === 2) {
      return valueArr[1];
    } else {
      return valueArr[valueArr.length - 1];
    }
  }

  private denormalizeSecondValue(filterType, value) {
    let selectedValue = null;
    switch (filterType) {
      case AutoGroupFilterEnum.COUNTRY:
      case AutoGroupFilterEnum.AUDIENCE_COUNTRY:
        const country = this.referenceData.countries.find(c => c.code === value.split('_')[0]);
        if (country) {
          selectedValue = country.id;
        }
        break;
      case AutoGroupFilterEnum.INFLUENCER_GENDER:
      case AutoGroupFilterEnum.AUDIENCE_GENDER:
        if (value.startsWith(AutoGroupFilterList.genders.male)) {
          selectedValue = GenderEnum.MALE;
        } else if (value.startsWith(AutoGroupFilterList.genders.female)) {
          selectedValue = GenderEnum.FEMALE;
        } else {
          selectedValue = GenderEnum.ONLINE_MEDIA;
        }
        break;
      case AutoGroupFilterEnum.AUDIENCE_AGE:
        const ageRange = AutoGroupFilterList.audienceAgeList.find(age => age.formatted === value.split('_')[0]);
        if (ageRange) {
          selectedValue = ageRange.id;
        }
        break;
      case AutoGroupFilterEnum.AVG_INTERACTION_PER_POST:
      case AutoGroupFilterEnum.AVG_ENGAGEMENT_PER_POST:
      case AutoGroupFilterEnum.AVG_REACH_PER_POST:
      case AutoGroupFilterEnum.FOLLOWERS:
      case AutoGroupFilterEnum.PLATFORM:
        selectedValue = value;
        break;
    }
    return selectedValue;
  }

  private getComparisonByOperator(operator, filterType) {
    const filterConfig = AutoGroupFilterConfig[filterType];
    if (!filterConfig) {
      return null;
    }
    const comparison = filterConfig.comparison.find(comp => AutoGroupComparisonConfig[comp].operator === operator);
    return comparison ? comparison : null;
  }

  /**
   * This method used to validate the condition list of Group Setting
   * There are some rules as defined:
   * Condition Type: MENTION -> (multiple comparision)
   * Condition Type: COUNTRY -> (multiple comparision and operator. However, every comparision and operator must be the same)
   * Condition Type: IMPACT_MONTHLY, ENGAGEMENT_MONTHLY, REACH_MONTHLY -> Single comparision and multiple operator
   * @param {GroupCondition[]} conditionList
   * @returns {boolean}
   */
  public validateConditions(conditionList: GroupCondition[]): ErrorTemplate[] {
    // define Error result
    const errors: ErrorTemplate[] = [];
    conditionList.forEach((condition: GroupCondition, index) => {
      switch (condition.selectedConditionType) {
        case GroupConditionTypeEnum.COUNTRY:
          const countryErrors = this.validateCountryCondition(index, conditionList);
          errors.push(...countryErrors);
          break;
        case GroupConditionTypeEnum.IMPACT_MONTHLY:
        case GroupConditionTypeEnum.ENGAGEMENT_MONTHLY:
        case GroupConditionTypeEnum.REACH_MONTHLY:
          const singleConditionErrors = this.validateSingeSelection(index, conditionList);
          errors.push(...singleConditionErrors);
          break;
      }
    });
    return errors;
  }

  private validateCountryCondition(index, conditionList: GroupCondition[]): ErrorTemplate[] {
    // define Error result
    const errors: ErrorTemplate[] = [];

    const countryConditions = conditionList.filter((condition) => condition.selectedConditionType === GroupConditionTypeEnum.COUNTRY);
    if (countryConditions.length <= 1) {
      return errors;
    }
    const [firstConditionOperator] = countryConditions;
    const haveDifferenceCondition = countryConditions.filter((con) => {
      return con.index !== firstConditionOperator.index
                && con.selectedConditionOperator !== firstConditionOperator.selectedConditionOperator;
    }).length > 0;

    if (haveDifferenceCondition) {
      errors.push(new ValidationError(`conditionListObject[${index}].selectedConditionType`, this.translate.instant('group_details.error_operator_country')));
      return errors;
    }

    const haveDifferenceComparision = countryConditions.filter((con) => {
      return con.index !== firstConditionOperator.index && con.selectedConditionComparision
        && con.selectedConditionComparision !== firstConditionOperator.selectedConditionComparision;
    }).length > 0;

    if (haveDifferenceComparision) {
      errors.push(new ValidationError(`conditionListObject[${index}].selectedConditionType`, this.translate.instant('group_details.error_comparision_country_difference')));
      return errors;
    }

    return errors;
  }

  private validateSingeSelection(index, conditionList: GroupCondition[]): ErrorTemplate[] {
    // define Error result
    const errors: ErrorTemplate[] = [];

    const reachConditions = conditionList.filter((condition) =>
                                  condition.selectedConditionType === GroupConditionTypeEnum.REACH_MONTHLY);
    if (reachConditions.length > 1) {
      errors.push(new ValidationError(`conditionListObject[${index}].selectedConditionType`, this.translate.instant('group_details.error_duplicate_condition_type')));
      return errors;
    }

    const engagementConditions = conditionList.filter((condition) =>
                                      condition.selectedConditionType === GroupConditionTypeEnum.ENGAGEMENT_MONTHLY);
    if (engagementConditions.length > 1) {
      errors.push(new ValidationError(`conditionListObject[${index}].selectedConditionType`, this.translate.instant('group_details.error_duplicate_condition_type')));
      return errors;
    }

    const impactConditions = conditionList.filter((condition) =>
                                      condition.selectedConditionType === GroupConditionTypeEnum.IMPACT_MONTHLY);

    if (impactConditions.length > 1) {
      errors.push(new ValidationError(`conditionListObject[${index}].selectedConditionType`, this.translate.instant('group_details.error_duplicate_condition_type')));
      return errors;
    }

    return errors;
  }

  public isConditionReachMonthly(conditionValue) {
    return conditionValue === GroupConditionTypeEnum.REACH_MONTHLY;
  }

  public isConditionEngagementMonthly(conditionValue) {
    return conditionValue === GroupConditionTypeEnum.ENGAGEMENT_MONTHLY;
  }

  public isConditionImpactMonthly(conditionValue) {
    return conditionValue === GroupConditionTypeEnum.IMPACT_MONTHLY;
  }

  public isConditionMention(conditionValue) {
    return conditionValue === GroupConditionTypeEnum.MENTION;
  }

  public isConditionCountry(conditionValue) {
    return conditionValue === GroupConditionTypeEnum.COUNTRY || conditionValue === GroupConditionTypeEnum.AUDIENCE_COUNTRY;
  }

  public isAgeCondition(conditionValue) {
    return conditionValue === GroupConditionTypeEnum.AUDIENCE_AGE;
  }

  public isAudienceGenderCondition(conditionValue) {
    return conditionValue === GroupConditionTypeEnum.AUDIENCE_GENDER;
  }

  public isInfluencerGenderCondition(conditionValue) {
    return conditionValue === GroupConditionTypeEnum.INFLUENCER_GENDER;
  }

  public isSingleComparision(conditionType) {
    return conditionType === GroupConditionTypeEnum.IMPACT_MONTHLY || conditionType === GroupConditionTypeEnum.ENGAGEMENT_MONTHLY
            || conditionType === GroupConditionTypeEnum.REACH_MONTHLY;
  }

  public loadInfluencerListOfGroup(groupSearch: GroupSearch) {
    return this.post(apiEndpoints.getInfluencerGroupInfluencerKPI, groupSearch);
  }

  public getInfluencerListByGroupId(groupSearch: GroupSearch, influencerGroupId: string) {
    return this.get(apiEndpoints.getInfluencerListByGroupId.replace(':groupId', influencerGroupId), groupSearch);
  }

  /**
   * this method use to load the influencer suggestion list by a query
   * user for AutoComplete component
   * @param {string} term
   * @returns {Observable<any>}
   */
  public loadInfluencerSuggestion(term: string, scrollId: string) {
    let params = {
      searchString: term,
    };
    if (scrollId) {
      params = Object.assign({}, params, { scrollId });
    }
    return this.get(apiEndpoints.searchInfluencer, params)
      .map(res => res.data)
      .catch(err => Observable.of([]));
  }

  /**
   * this method use to get the details of Influencer by id
   * @param {number} influencerId
   * @returns {Observable<Influencer>}
   */
  public getInfluencerDetails(influencerId: number): Observable<Influencer> {
    return this.get(apiEndpoints.getInfluencerById + influencerId).map((res: any) => {
      if (!res || !res.data) {
        return null;
      }
      const influencer: Influencer = new Influencer();
      influencer.id = res.data.id;
      influencer.influencer_name = res.data.name;
      influencer.country_code = res.data.country.code;
      influencer.influencerChannels = res.data.influencerChannels;
      return influencer;
    });
  }


  /**
   * this method use to add an influencer to group
   * @param influencerGroupAdding
   * @returns {Observable<any>}
   */
  public addInfluencerToGroup(influencerGroupAdding: any): Observable<any> {
    return this.post(apiEndpoints.saveInfluencerManualAddedToInfluencerGroup, influencerGroupAdding);
  }

  /**
   * add a list of Influencer to specific group as pending
   * @param influencerGroupAdding
   * @returns {Observable<any>}
   */
  public addInfluencerToGroupAsPending(influencerGroupAdding: AddInfluencerGroup): Observable<any> {
    return this.post(apiEndpoints.addPendingInfluencerToGroup.replace(':groupId', influencerGroupAdding.influencer_group_id.toString()), {
      influencerList: influencerGroupAdding.influencerList,
      group_id: influencerGroupAdding.influencer_group_id,
      reviewerList: influencerGroupAdding.reviewList
    });
  }

  /**
   * this method use to move multiple influencers to a group
   * @param influencerGroupAdding
   * @returns {Observable<any>}
   */
  public moveInfluencerToGroup(influencerGroupId, influencers, newGroupId, companyId) {
    return this.put(apiEndpoints.moveInfluencersToAnotherGroup.replace(':groupId', influencerGroupId),
      {influencers, newGroupId, companyId});
  }

  /**
   * this method use to delete one influencer in a group
   * @param {number} influencerId
   * @param {number} influencerGroupId
   * @param {number} companyId
   * @returns {Observable<any>}
   */
  public deleteInfluencerOfGroup(influencerIds: number[], influencerGroupId: any, companyId: number): Observable<any> {
    const influencerDeleteData = {
      companyId: companyId,
      influencers: influencerIds,
    };
    return this.put(apiEndpoints.removeInfluencers.replace(':groupId', influencerGroupId), influencerDeleteData);
  }

  /**
   * Approve a pending influencer in group
   * @param {number} influencerId
   * @param influencerGroupId
   * @param {number} companyId
   * @returns {Observable<any>}
   */
  public approveInfluencerInGroup(influencerIds: number[], influencerGroupId: any, companyId: number) {
    const influencerToApprove = {
      companyId: companyId,
      influencers: influencerIds,
    };
    return this.put(apiEndpoints.approveInfluencers.replace(':groupId', influencerGroupId), influencerToApprove);
  }

  getInfluencerGroups(companyId): Observable<any[]> {
    return this.get(apiEndpoints.getInfluencerGroupList, {selected_company_id: companyId}).map((res: any) => res ? res.data : []);
  }

  /**
   * this method is used to get the list of influencer who are belonged to existing group
   * @param groupId
   * @returns {Observable<any>}
   */
  public getListInfluencerBelongToGroup(groupId) {
    const apiEndpoint  = apiEndpoints.getManualAddedInfluencerList.replace(':groupId', groupId);
    return this.get(apiEndpoint);
  }

  /**
 * this method is used to get monthly visit influencer
 * @returns {Observable<any>}
 */
  public getMonthlyVisitInfluencer(companyId, limit, offset) {
    const result = this.get(apiEndpoints.getMonthlyVisitInfluencer + `?companyId=${companyId}&limit=${limit}&offsetId=${offset}`);
    return result.map(res => res && res.data ? res.data : null);
  }

  /**
   * this method is used to get pending influencer list
   * @returns {Observable<any>}
  */
  public getPendingInfluencerList(groupId, limit, lastSeenId) {
    const result = this.get(apiEndpoints.getPendingInfluencerList.replace(':groupId', groupId) + `?limit=${limit}&lastSeenId=${lastSeenId}`);
    return result.map(res => res && res.data ? res.data : null);
  }

  public getInfluencerGroupSourceTypes() {
    return this.get(apiEndpoints.getInfluencerGroupSourceTypes).map(res => {
      if (res && res.data) {
        return res.data.map(source => ({
          id: source.id,
          name: this.translate.instant(`new_group_settings.auto_group_sources.sources.${source.label}.value`),
          value: this.translate.instant(`new_group_settings.auto_group_sources.sources.${source.label}.dropdown`),
        }));
      }
    });
  }

  public getInfluencerGroupFilterTypes() {
    return this.get(apiEndpoints.getInfluencerGroupFilterTypes).map(res => {
      if (res && res.data) {
        return res.data.map(filter => ({
          id: filter.id,
          name: this.translate.instant(`new_group_settings.auto_group_filters.filter.${filter.label}.value`),
          value: this.translate.instant(`new_group_settings.auto_group_filters.filter.${filter.label}.dropdown`),
        }));
      }
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/services/group-details/group.service.ts